import { useEffect, useRef, useState, useCallback } from "react";

const ICE = [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }];
const API = process.env.REACT_APP_BACKEND_URL;

// Multi-party mesh over the backend WebSocket relay. The newcomer offers to every existing peer.
export function useMesh({ code, pid, token, enabled = true }) {
  const [localStream, setLocalStream] = useState(null);
  const [peers, setPeers] = useState({});
  const [camError, setCamError] = useState("");
  const [lastMessage, setLastMessage] = useState(null);
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const pcs = useRef({});
  const ws = useRef(null);
  const local = useRef(null);
  const ice = useRef(ICE);

  const send = useCallback((m) => { if (ws.current?.readyState === 1) ws.current.send(JSON.stringify(m)); }, []);

  const makePc = useCallback((peerId, meta) => {
    if (pcs.current[peerId]) return pcs.current[peerId];
    const p = new RTCPeerConnection({ iceServers: ice.current });
    local.current?.getTracks().forEach((t) => p.addTrack(t, local.current));
    p.onicecandidate = (e) => e.candidate && send({ type: "ice", to: peerId, candidate: e.candidate });
    p.ontrack = (e) => setPeers((prev) => ({ ...prev, [peerId]: { ...(prev[peerId] || {}), ...meta, stream: e.streams[0] } }));
    p.onconnectionstatechange = () => { if (["failed", "closed"].includes(p.connectionState)) dropPeer(peerId); };
    pcs.current[peerId] = p;
    setPeers((prev) => ({ ...prev, [peerId]: { ...(prev[peerId] || {}), ...meta } }));
    return p;
    // eslint-disable-next-line
  }, [send]);

  const dropPeer = (peerId) => {
    pcs.current[peerId]?.close(); delete pcs.current[peerId];
    setPeers((prev) => { const n = { ...prev }; delete n[peerId]; return n; });
  };

  const toggleMute = useCallback((force) => {
    const next = typeof force === "boolean" ? force : !muted;
    local.current?.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setMuted(next);
  }, [muted]);
  const toggleCam = useCallback(() => {
    local.current?.getVideoTracks().forEach((t) => { t.enabled = camOff; });
    setCamOff(!camOff);
  }, [camOff]);

  useEffect(() => {
    if (!enabled || !code || !pid || !token) return undefined;
    let closed = false;
    (async () => {
      try { const r = await fetch(`${API}/api/public/ice-servers`); const d = await r.json(); ice.current = d.iceServers || ICE; } catch {}
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 640 } }, audio: true });
        if (closed) { s.getTracks().forEach((t) => t.stop()); return; }
        local.current = s; setLocalStream(s);
      } catch { setCamError("Camera or microphone unavailable. Check permissions."); }
      const base = API.replace(/^http/, "ws");
      const sock = new WebSocket(`${base}/api/ws/group/${code}?pid=${encodeURIComponent(pid)}&t=${encodeURIComponent(token)}`);
      ws.current = sock;
      sock.onmessage = async (ev) => {
        const m = JSON.parse(ev.data);
        setLastMessage({ ...m, _t: Date.now() });
        if (m.type === "peers") {
          for (const peer of m.peers) {
            const p = makePc(peer.id, { name: peer.name, role: peer.role });
            const offer = await p.createOffer();
            await p.setLocalDescription(offer);
            send({ type: "offer", to: peer.id, sdp: offer, name: undefined });
          }
        } else if (m.type === "peer_joined") {
          setPeers((prev) => ({ ...prev, [m.from]: { ...(prev[m.from] || {}), name: m.name, role: m.role } }));
        } else if (m.type === "offer") {
          const p = makePc(m.from, {});
          await p.setRemoteDescription(m.sdp);
          const ans = await p.createAnswer();
          await p.setLocalDescription(ans);
          send({ type: "answer", to: m.from, sdp: ans });
        } else if (m.type === "answer") {
          await pcs.current[m.from]?.setRemoteDescription(m.sdp);
        } else if (m.type === "ice") {
          try { await pcs.current[m.from]?.addIceCandidate(m.candidate); } catch {}
        } else if (m.type === "peer_left") {
          dropPeer(m.from);
        } else if (m.type === "mute") {
          local.current?.getAudioTracks().forEach((t) => { t.enabled = false; }); setMuted(true);
        }
      };
    })();
    return () => {
      closed = true;
      ws.current?.close();
      Object.values(pcs.current).forEach((p) => p.close()); pcs.current = {};
      local.current?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line
  }, [code, pid, token, enabled]);

  return { localStream, peers, camError, send, lastMessage, muted, camOff, toggleMute, toggleCam };
}

export const VideoTile = ({ stream, label, sub, muted = false, mirror = false, children }) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && stream) ref.current.srcObject = stream; }, [stream]);
  return (
    <div className="relative aspect-video overflow-hidden rounded-2xl bg-black" data-testid="video-tile">
      {stream ? <video ref={ref} autoPlay playsInline muted={muted} className={`h-full w-full object-cover ${mirror ? "scale-x-[-1]" : ""}`} /> : <div className="flex h-full items-center justify-center text-xs text-white/60">Connecting…</div>}
      <div className="absolute bottom-2 left-2 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white">{label}{sub && <span className="text-white/60"> · {sub}</span>}</div>
      {children}
    </div>
  );
};
